import * as React from 'react'
import { Platform, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Tabs from '@components/tabs/Tabs'
import Icon from '@components/icon/Icons'

import useNavigation from '@layout/useNavigation'
import useI18n from '@i18n/useI18n'

import { isBefore, isSameYear } from 'date-fns'
import { firstLetterUppercase } from '@utils/stringUtils'

import theme from '@theme/Theme'

const VERTICAL_PADDING = 16
const TAB_HEIGHT = 30

interface ListOffset {
  id: string
  offset: number
}

interface Props {
  scroll: React.RefObject<ScrollView>
  scrollHeight: number
  sections: ManifestationsSection[]
}

const ClosedManifestationsSections = ({ scroll, scrollHeight, sections }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const [offsets, setOffsets] = React.useState<ListOffset[]>([])

  const [contentHeight, setContentHeight] = React.useState(0)

  const orderedByYear = React.useMemo(
    () =>
      sections
        .reduce((acc, cur) => {
          const index = acc.findIndex((section) => isSameYear(new Date(section.title), new Date(cur.section)))
          if (index > -1) {
            acc[index].sections.push(cur)
            return acc
          }
          return [...acc, { title: cur.section, sections: [cur] }]
        }, [] as { title: string; sections: ManifestationsSection[] }[])
        .sort((a, b) => (isBefore(new Date(a.title), new Date(b.title)) ? 1 : -1)),
    [sections]
  )

  const tabs = React.useMemo(
    () =>
      orderedByYear.map((year) => ({
        label: i18n.t('screens.manifestations.year', { date: new Date(year.title) }),
        value: year.title,
      })),
    [orderedByYear]
  )

  const displayTabs = tabs.length > 0 && contentHeight > scrollHeight

  const onTabItemSelected = (opt: TabOption) => {
    if (scroll.current) {
      const offset = offsets.find((off) => off.id === opt.value)
      if (!!offset) {
        scroll.current.scrollTo({
          y: offset.offset + TAB_HEIGHT + 3 * VERTICAL_PADDING + 25,
          animated: true,
        })
      }
    }
  }

  const onMonthClicked = (section: ManifestationsSection) => {
    navigation.push('/manifestationMonth', { section })
  }

  const renderMonthCard = (section: ManifestationsSection) => (
    <Container key={section.section} activeOpacity={0.9} onPress={() => onMonthClicked(section)}>
      <SafeContainer>
        <TopContainer>
          <Name numberOfLines={2} ellipsizeMode="tail">
            {firstLetterUppercase(i18n.t('screens.manifestations.sectionDate', { date: new Date(section.section) }))}
          </Name>
          <Events>{i18n.t('screens.manifestations.event', { count: section.manifestations.length })}</Events>
        </TopContainer>
        <IconContainer>
          <Icon name="arrow" size={24} color={theme.colors.primary} />
        </IconContainer>
      </SafeContainer>
    </Container>
  )

  const renderYear = (year: { title: string; sections: ManifestationsSection[] }) => (
    <YearContainer
      key={year.title}
      onLayout={(e) => {
        if (Platform.OS !== 'web') {
          e.persist()
        }
        if (!!e && !!e.nativeEvent) {
          setOffsets((off) => [...off, { id: year.title, offset: e?.nativeEvent?.layout?.y }])
        }
      }}>
      <SectionYear>{i18n.t('screens.manifestations.year', { date: new Date(year.title) })}</SectionYear>
      {year.sections.map(renderMonthCard)}
    </YearContainer>
  )

  return (
    <View
      onLayout={(e) => {
        if (Platform.OS !== 'web') {
          e.persist()
        }
        setContentHeight(e.nativeEvent.layout.height)
      }}>
      {displayTabs && (
        <TabContainer>
          <Tabs data={tabs} value={tabs[0]} onItemSelected={onTabItemSelected} equalTabs={false} />
        </TabContainer>
      )}
      {orderedByYear.map(renderYear)}
    </View>
  )
}

export default ClosedManifestationsSections

const SectionYear = styled(Text)`
  margin-bottom: 8px;
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Headline : props.theme.fonts.Body)};
  ${(props) => props.theme.breakpoint !== 'phone' && `color: ${props.theme.colors.secondaryText};`}
`
const TabContainer = styled(View)`
  margin-bottom: ${VERTICAL_PADDING}px;
  height: ${TAB_HEIGHT}px;
`

const Container = styled(TouchableOpacity)`
  border-radius: 8px;
  background-color: white;
  margin-bottom: 8px;
  ${(props) => props.theme.constants.shadow}
`
const SafeContainer = styled(View)`
  border-radius: 8px;
  overflow: hidden;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'row' : 'column')};
  ${(props) => props.theme.breakpoint === 'phone' && `align-items: center;`}
`
const TopContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '16px 24px')};
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.breakpoint === 'phone' && `flex: 1;`}
`
const Name = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
  ${(props) => props.theme.breakpoint === 'phone' && `margin-bottom: 4px;`}
`
const Events = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
`
const IconContainer = styled(View)`
  margin: 0 16px;
`
const YearContainer = styled(View)`
  margin-bottom: ${VERTICAL_PADDING}px;
`
